import { useRef, useCallback, useEffect } from 'react'

interface ThrottleOptions {
  leading?: boolean
  trailing?: boolean
}

export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  options: ThrottleOptions = { leading: true, trailing: true }
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastRunRef = useRef<number>(0)
  const pendingArgsRef = useRef<Parameters<T> | null>(null)
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);


  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  const throttledFn = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now()
      const timeSinceLastRun = now - lastRunRef.current

      if (options.leading && timeSinceLastRun >= delay) {
        callbackRef.current(...args)
        lastRunRef.current = now

        return
      }

      pendingArgsRef.current = args;

      if (timeoutRef.current) {
        return;
      }

      if (options.trailing) {
        const remainingTime = Math.max(delay - timeSinceLastRun, 0);
        timeoutRef.current = setTimeout(() => {
          if (pendingArgsRef.current !== null) {
            callbackRef.current(...pendingArgsRef.current);
            pendingArgsRef.current = null;
          }
          lastRunRef.current = Date.now();
          timeoutRef.current = null;
        }, remainingTime);
      }
    },
    [delay, options.leading, options.trailing]
  );

  return throttledFn as T
}